// El carrito del microsite frente al menú FRESCO.
//
// La carta se lee una vez al cargar la página (en producción, además, desde un
// cache por slug) y el carrito vive en el navegador. Si el dueño agota un plato o
// un ingrediente mientras el cliente arma su pedido, el carrito lo sigue
// ofreciendo, y el cliente se enteraba recién en el pago: /session respondía 409
// y "Reintentar" mandaba el mismo carrito. Esto decide qué líneas ya no se pueden
// vender, para quitarlas y avisar ANTES de cobrar.
//
// Platos e ingredientes usan el criterio del kiosko (lib/kiosk/cartReconcile): una
// sola definición de "esto ya no se vende", con la misma excepción para lo que se
// QUITA. Encima va la ventana horaria, que el kiosko no necesita porque su menú se
// refresca solo.
//
// Es una AYUDA, no la puerta: /session vuelve a validar todo antes de cobrar.

import { reconcileCart } from '@/lib/kiosk/cartReconcile'
import { isItemAvailableNow } from '@/lib/menu/availability'
import type { CartItem } from '@/components/microsite/types'
import { micrositeLineName, type MicrositeLocale } from '@/lib/microsite/couponUx'

export type CartAvailabilityOption = {
  id: string
  name?: string | null
  price_delta?: number | null
  is_available?: boolean | null
}

/** Un plato tal como lo devuelve /api/online-orders/cart-availability. */
export type CartAvailabilityItem = {
  id: string
  name?: string | null
  is_available: boolean | null
  is_active?: boolean | null
  available_from?: string | null
  available_until?: string | null
  available_days?: number[] | null
  item_modifier_groups?: ReadonlyArray<{
    modifier_groups?: {
      display_style?: string | null
      modifier_options?: ReadonlyArray<CartAvailabilityOption> | null
    } | null
  }> | null
}

export type CartAvailabilitySnapshot = {
  /** Zona horaria del local, para la ventana de venta. */
  timezone: string | null
  items: CartAvailabilityItem[]
}

/**
 * Las líneas del carrito que el menú fresco ya no deja vender, en el orden del
 * carrito, con el nombre para el aviso: "Plato" si se agotó, se ocultó o salió de
 * su horario; "Plato (Ingrediente)" si fue un ingrediente agregado.
 *
 * Súper online (minor 5 de la revisión final): el nombre es el de la línea en el
 * carrito (`micrositeLineName`): «Leche · Galón», «Banana · 1,5 lb». Una línea de
 * restaurante (sin peso ni variante) devuelve `name` tal cual: el aviso de main.
 */
export function unsellableCartLines(
  cart: ReadonlyArray<
    Pick<CartItem, 'cartId' | 'itemId' | 'name' | 'modifiers' | 'weightMilli' | 'weightUnit' | 'variantId' | 'variantName'>
  >,
  snapshot: CartAvailabilitySnapshot,
  now: Date = new Date(),
  locale: MicrositeLocale = 'es',
): Array<{ cartId: string; name: string }> {
  // Oculto del menú (`is_active=false`) cuenta igual que borrado: ya no está en la carta.
  const visibles = snapshot.items.filter((i) => i.is_active !== false)
  const lineName = (l: (typeof cart)[number]) => micrositeLineName(l, locale)
  const { removed } = reconcileCart(
    cart.map((l) => ({ cart_id: l.cartId, item_id: l.itemId, name: lineName(l), modifiers: l.modifiers })),
    visibles,
  )
  const motivo = new Map(removed.map((r) => [r.cart_id, r.name]))

  const tz = snapshot.timezone ?? 'UTC'
  const porId = new Map(visibles.map((i) => [i.id, i]))
  for (const linea of cart) {
    if (motivo.has(linea.cartId)) continue
    const item = porId.get(linea.itemId)
    if (item && !isItemAvailableNow(item, tz, now)) motivo.set(linea.cartId, lineName(linea))
  }

  return cart.flatMap((l) => {
    const name = motivo.get(l.cartId)
    return name ? [{ cartId: l.cartId, name }] : []
  })
}

/**
 * Rechazos del súper online (Tarea 2b.3, R2), por status: stock que se acabó y
 * líneas retail que la tienda no puede vender así. Un restaurante nunca los recibe
 * (salen solo de productos `is_retail`). Vuelven al carrito con el `message` del
 * servidor, que nombra el producto: el menú fresco no los detecta.
 */
const RETAIL_REJECTIONS: Record<number, readonly string[]> = {
  409: ['out_of_stock'],
  422: [
    'item_not_sold_online',
    'retail_not_supported_online',
    'variant_required',
    'weight_required',
    'weight_not_allowed',
    'weight_invalid',
    'retail_modifiers_not_supported',
  ],
}

/** Errores de /session que significan "algo del carrito ya no se vende", por status. */
const AVAILABILITY_REJECTIONS: Record<number, readonly string[]> = {
  409: ['items_unavailable', 'modifiers_unavailable', ...RETAIL_REJECTIONS[409]],
  422: ['item_unavailable', ...RETAIL_REJECTIONS[422]],
}

/**
 * ¿Esta respuesta de /session es un rechazo por disponibilidad del menú? Llegan
 * ANTES de cobrar y se resuelven en el carrito, no reintentando el mismo pedido.
 * Los de envío y cupón tienen su propio camino; todo lo demás sigue siendo un
 * error de pago.
 */
export function isSessionAvailabilityRejection(status: number, body: unknown): boolean {
  const error = (body as { error?: unknown } | null)?.error
  return typeof error === 'string' && (AVAILABILITY_REJECTIONS[status] ?? []).includes(error)
}

/**
 * El mensaje en prosa de un rechazo RETAIL de /session (p. ej. «Se nos acabó:
 * Banana. Ajusta tu carrito.»), para mostrarlo en el carrito. null para cualquier
 * otro rechazo —los de restaurante siguen con su aviso de siempre— o sin mensaje.
 */
export function retailRejectionMessage(status: number, body: unknown): string | null {
  const b = body as { error?: unknown; message?: unknown } | null
  if (typeof b?.error !== 'string' || !(RETAIL_REJECTIONS[status] ?? []).includes(b.error)) return null
  return typeof b.message === 'string' && b.message.trim() ? b.message : null
}
